/** Цвета: диапазоны «запас, дней» (4 фиксированных + цвет «нет продаж») и палитра
 * подсветки правок производства (53 недели). Порт секций tech-вкладки (стр. 8139-8153). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import {
  mergeChangeColors,
  mergeStockDaySettings,
  type StockDaySettings,
} from "../settings-defaults";

export function ColorsEditor({ canEdit }: { canEdit: boolean }): React.ReactElement {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["orders", "refs", "settings"], queryFn: api.listSettings });
  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => api.updateSetting(key, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "refs", "settings"] });
      toast.success("Настройка сохранена");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Не удалось сохранить настройку")),
  });

  const savedMap = new Map((settings.data ?? []).map((s) => [s.key, s.value]));
  const stockDays = mergeStockDaySettings(savedMap.get("stock_day_settings"));
  const changeColors = mergeChangeColors(savedMap.get("production_change_colors"));

  if (settings.isLoading) {
    return <div className="text-sm text-muted-foreground">Загружаю настройки…</div>;
  }

  return (
    <div className="space-y-3">
      <StockDaysSection
        value={stockDays}
        canEdit={canEdit}
        onSave={(v) => save.mutate({ key: "stock_day_settings", value: v })}
      />
      <ChangeColorsSection
        value={changeColors}
        canEdit={canEdit}
        onSave={(v) => save.mutate({ key: "production_change_colors", value: v })}
      />
    </div>
  );
}

function StockDaysSection({
  value,
  canEdit,
  onSave,
}: {
  value: StockDaySettings;
  canEdit: boolean;
  onSave: (v: StockDaySettings) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const setRange = (i: number, patch: Partial<StockDaySettings["ranges"][number]>): void =>
    setDraft((d) => ({
      ...d,
      ranges: d.ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)),
    }));

  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-2 text-sm font-medium">Цвета «Запас, дней»</div>
      <div className="space-y-1.5">
        {draft.ranges.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <input
              type="number"
              min={0}
              className="h-7 w-20 rounded border bg-background px-1.5 text-right"
              disabled={!canEdit}
              value={r.from}
              onChange={(e) => setRange(i, { from: Number(e.target.value) })}
            />
            <span className="text-muted-foreground">—</span>
            <input
              type="number"
              min={0}
              className="h-7 w-20 rounded border bg-background px-1.5 text-right"
              disabled={!canEdit}
              value={r.to}
              onChange={(e) => setRange(i, { to: Number(e.target.value) })}
            />
            <input
              type="color"
              className="size-7 cursor-pointer rounded border"
              disabled={!canEdit}
              value={r.color}
              onChange={(e) => setRange(i, { color: e.target.value })}
            />
            <input
              className="h-7 w-40 rounded border bg-background px-1.5"
              disabled={!canEdit}
              value={r.label}
              onChange={(e) => setRange(i, { label: e.target.value })}
            />
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1 text-xs">
          <span className="w-[172px] text-muted-foreground">Есть остаток, нет продаж:</span>
          <input
            type="color"
            className="size-7 cursor-pointer rounded border"
            disabled={!canEdit}
            value={draft.noSalesColor}
            onChange={(e) => setDraft((d) => ({ ...d, noSalesColor: e.target.value }))}
          />
        </div>
      </div>
      {canEdit && (
        <Button size="sm" className="mt-2" onClick={() => onSave(draft)}>
          Сохранить
        </Button>
      )}
    </div>
  );
}

function ChangeColorsSection({
  value,
  canEdit,
  onSave,
}: {
  value: string[];
  canEdit: boolean;
  onSave: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <div className="rounded-md border bg-card p-3">
      <div className="mb-1 text-sm font-medium">Цвета правок плана производства</div>
      <div className="mb-2 text-[11px] text-muted-foreground">
        Цвет ячейки зависит от недели, в которую сделана правка (Н1–Н53).
      </div>
      <div className="grid grid-cols-8 gap-1.5 sm:grid-cols-12 xl:grid-cols-18">
        {draft.map((color, i) => (
          <label key={i} className="flex flex-col items-center gap-0.5 text-[9px] text-muted-foreground">
            <input
              type="color"
              className="size-6 cursor-pointer rounded border"
              disabled={!canEdit}
              value={color}
              onChange={(e) =>
                setDraft((d) => d.map((c, j) => (j === i ? e.target.value : c)))
              }
            />
            Н{i + 1}
          </label>
        ))}
      </div>
      {canEdit && (
        <Button size="sm" className="mt-2" onClick={() => onSave(draft)}>
          Сохранить
        </Button>
      )}
    </div>
  );
}
